<script setup>
// 앱 섹션 — 반응형으로 옮긴 네 번째 섹션.
//
// 원본: Figma 홈(ver1) 4133:20301 (1920x1009)
//   배경  linear-gradient(119.018deg, #000 1.133%, #221602 174.35%)
//   왼쪽 글 x 215 w 641, 세로 가운데
//     눈썹   15px semibold, 테두리 #ffe3af, 알약 (아래 간격 20)
//     제목   60px bold 행간 70, 흰색->#ede1ca 그라데이션 글자, 3줄 (아래 간격 50)
//     부제   22px 행간 30, 1줄 normal + 2줄 bold (아래 간격 50)
//     버튼   200x54 두 개, 간격 16, 아이콘 34, 11px/16px
//   오른쪽 핸드폰 x 789 w 1031, 그림 y 130~1128
//
// 원본에서 바꾼 것
//   1) 절대좌표를 걷어내고 두 칸으로 담았다. 1024 아래에서는 한 칸으로 접힌다.
//   2) 제목만 clamp 로 감쌌다. 1280 이상은 원본 60px 에 걸려 더 커지지 않는다.
//      나머지 글자(15 / 22 / 16 / 11px)는 전부 원본 값 그대로 고정이다.
//   3) 핸드폰은 글이 아니라 그림이라 통째로 줄인다. 0.62 배다.
//      마스크로 그린 구조라 안쪽은 원본 좌표 그대로 둔다.
//      한때 0.86 이었는데 글보다 너무 커 보여 줄였다(2026-09-26 요청) — 폭이 글 칸의
//      1.38 배, 높이가 1.72 배였다. 0.62 면 폭이 639 로 글 칸(641)과 같아진다.
//
// 배경은 제 것을 갖지 않는다(bare) — 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDesignScale } from '@/composables/useDesignScale.js'
import {
  SCENE_TEXT_STAGGER_MS,
  APP_PHONE_SLIDE_PX,
  SCENE_REVEAL_DELAY_MS,
  SCENE_APPEAR_MS,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'
import imgChangeColorHere from '@/assets/figma/2280741506751ea9a7650ceeeadf4f4fc1fc857a.png'
import imgDisplayFrame from '@/assets/figma/10be8110865394191da0a1a0acbe7fb243194e56.png'
import img from '@/assets/figma/13cfb92d3668c49742140a255e721e64f13be485.png'
import img1 from '@/assets/figma/e82ed13d86cf85c8d8ec4385c26f03c5adeb5ba0.png'
import imgChangeColorHere1 from '@/assets/figma/496634469662b6769abc4b02875368b74764475d.png'
import imgDisplayFrame1 from '@/assets/figma/80a14c9f52232ff860d2014c687d3e473bb452ba.png'
import img2 from '@/assets/figma/1bd5126130146e1e2edd8355f31d0d6c8a8adcf6.png'
import img3 from '@/assets/figma/706ba64227216c2f3ea2ce3d6e68bbcdb1f13c66.png'
import imgGroup2117905615 from '@/assets/figma/f5e5d48c9cbfd5276b4931918b6fc5ea8c373dda.svg'
import imgGroup2117905616 from '@/assets/figma/11163d5f4581b9806df5678f7354784ad87105be.svg'
import imgFix from '@/assets/figma/02e2e25b4ea7511952eb79ec439eb34da4121bbe.svg'
import imgFix1 from '@/assets/figma/02f3bd74caa5bfcd9af1fe14d92685492afd4033.svg'
import imgFix2 from '@/assets/figma/4b709ff45ebf9304cece65af5b8c52edd1ec0c3b.svg'
import imgFix3 from '@/assets/figma/f9f6785f482da85c4a4220965c24dc0c53a6370f.svg'

const style1 = { backgroundImage: "linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 100%)" }
const style2 = { backgroundImage: "linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 100%)" }
const style3 = { maskImage: `url("${imgFix}")` }
const style4 = { maskImage: `url("${imgFix}")` }
const style5 = { maskImage: `url("${imgFix}")` }
const style6 = { maskImage: `url("${imgFix}"), url("${img}")` }
const style7 = { maskImage: `url("${imgFix2}")` }
const style8 = { maskImage: `url("${imgFix2}")` }
const style9 = { maskImage: `url("${imgFix2}")` }
const style10 = { maskImage: `url("${imgFix2}"), url("${img2}")` }

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 39 },
  // 배경을 끄고 나온다.
  bare: { type: Boolean, default: false },
})

// 세로 리듬 배율. 여백을 창 폭에 맞춰 줄인다 — 글자는 건드리지 않는다.
// 자세한 이유는 composables/useDesignScale.js 에 있다.
const root = ref(null)
const { scale: rhythm } = useDesignScale(root)

const FIRST_FRAME = 37
const revealed = computed(() => props.step >= FIRST_FRAME)

// rise = 아래에서 위로, slideX = 양수면 오른쪽에서 왼쪽으로.
// 글은 모든 섹션이 같은 효과를 쓴다.
const curtain = (order) => sceneTextReveal(revealed.value, order)

// 핸드폰 그림은 옆에서 미끄러져 들어오는 제 연출을 그대로 둔다.
const appear = (order, { rise = 0, slideX = 0 } = {}) => ({
  opacity: revealed.value ? 1 : 0,
  transform: revealed.value ? 'none' : `translate(${slideX}px, ${rise}px)`,
  transitionProperty: 'opacity, transform',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
  transitionDuration: `${SCENE_APPEAR_MS}ms`,
  transitionDelay: revealed.value
    ? `${SCENE_REVEAL_DELAY_MS + order * SCENE_TEXT_STAGGER_MS}ms`
    : '0ms',
})

// ── 핸드폰 배율 ──────────────────────────────────────────────────────
// 그림의 실제 크기는 1031 x 998 이다(묶음 상자는 879 지만 안쪽이 998 까지 내려간다).
//
// 0.62 는 왼쪽 글 칸(641)과 폭을 맞추는 값이다 — 1031 x 0.62 = 639.
// 높이는 619 로 글(500)의 1.24 배가 된다.
//
// 전에는 0.86 이었다. "섹션 높이 1009 안에 998 이 들어오게" 하려고 고른 값인데,
// 그 1009 는 원본 캔버스 높이일 뿐이고 지금은 섹션 높이를 내용이 정한다
// (상한 + 리듬). 붙잡을 이유가 없어졌고, 글보다 너무 커 보였다.
const PHONE_W = 1031
const PHONE_H = 998
const PHONE_MAX_SCALE = 0.62
// 핸드폰 그림 상자(619) 안에서 픽셀이 실제로 칠해지는 자리는 30~598 이다.
// 남는 위아래를 마진으로 당겨 콘텐츠 상자가 그림에 딱 맞게 한다(2026-09-26).
const PHONE_PAD_TOP = 30
const PHONE_PAD_BOTTOM = 21

const phoneCol = ref(null)
const phoneScale = ref(PHONE_MAX_SCALE)
let observer = null

const measurePhone = () => {
  const w = phoneCol.value?.clientWidth
  if (!w) return
  // 배율은 화면 뿌리(HomeSceneView)의 zoom 이 전담한다. 칸 폭은 zoom 안에서 항상
  // 1920 기준(944)이라 여기서는 늘 최대치다. 재면 오히려 zoom 이 걸리기 전 값
  // (1536 창에서 835)으로 굳어 이 섹션만 0.75 배가 된다.
  phoneScale.value = PHONE_MAX_SCALE
}

onMounted(() => {
  measurePhone()
  observer = new ResizeObserver(measurePhone)
  observer.observe(phoneCol.value)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <section
    ref="root"
    class="relative flex w-full items-center justify-center overflow-hidden px-[20px]"
    :style="{ '--r': rhythm }"
    :class="{ 'app-bg': !bare }"
    data-node-id="4133:20301"
  >
    <div
      class="mx-auto flex w-full max-w-[1530px] flex-col items-start gap-[60px] px-[20px] lg:flex-row lg:items-center lg:gap-[20px]"
    >
      <!-- 왼쪽 글 -->
      <div class="w-full max-w-[641px] shrink-0 lg:w-[641px]" data-node-id="4133:20303">
        <span class="app-eyebrow" :style="curtain(0)" data-node-id="4133:20305">Real Gold Trade</span>

        <h2 class="app-title mt-[30px]" :style="curtain(0)" data-node-id="4133:20307">
          내 손안의 손쉬운 거래<br />대한민국 대표<br />금, 은 중매거래
        </h2>

        <div class="mt-[50px]" :style="curtain(1)" data-node-id="4133:20314">
          <p class="app-sub">Real Money is Real Gold Trad</p>
          <p class="app-sub font-bold">AUGO</p>
        </div>

        <div
          class="mt-[50px] flex flex-wrap gap-[16px]"
          :style="curtain(1)"
          data-node-id="4133:20319"
        >
          <span class="app-store" :style="style1">
            <span class="relative block size-[34px] shrink-0">
              <span class="absolute inset-[5.42%_15.01%_10.57%_14.98%] block">
                <img alt="" class="block size-full" :src="imgGroup2117905615" />
              </span>
            </span>
            <span class="block">
              <span class="app-store-top">Download here</span>
              <span class="app-store-name">App Store</span>
            </span>
          </span>
          <span class="app-store" :style="style2">
            <span class="relative block size-[34px] shrink-0">
              <span class="absolute inset-[7.73%_7.38%_7.74%_18.66%] block">
                <img alt="" class="block size-full" :src="imgGroup2117905616" />
              </span>
            </span>
            <span class="block">
              <span class="app-store-top">Download here</span>
              <span class="app-store-name">Google Play</span>
            </span>
          </span>
        </div>
      </div>

      <!-- 오른쪽 핸드폰. 글이 아니라 그림이라 통째로 줄인다. -->
      <!-- 위아래 마진은 그림 자체의 투명 가장자리를 상쇄한다(2026-09-26).
           상자는 619 인데 픽셀이 실제로 칠해지는 자리는 30~598 이라, 그대로 두면
           섹션 위아래에 빈 자리가 남아 앞뒤 경계의 간격이 그만큼 벌어진다.
           이 칸이 섹션 높이를 정하므로(글 칸이 더 짧다) 여기만 당기면 된다. -->
      <div
        ref="phoneCol"
        class="relative w-full min-w-0 flex-1"
        :style="{
          height: `${PHONE_H * phoneScale}px`,
          marginTop: `${-PHONE_PAD_TOP}px`,
          marginBottom: `${-PHONE_PAD_BOTTOM}px`,
        }"
      >
        <!-- 칸 오른쪽 끝에 붙인다(2026-09-26 제보: 앱 섹션이 왼쪽으로 쏠려 보인다).
             한때 칸 가운데에 뒀는데, 그러면 왼쪽 여백 204 · 오른쪽 300 으로 어긋나
             글만 멀리 떨어져 보였다. 오른쪽에 붙이면 좌우가 204 / 205 로 맞는다.
             원본도 핸드폰이 x1820 까지 가서 오른쪽에 붙어 있었다.
             origin-top-left 로 줄이므로 그림이 차지하는 자리는 왼쪽 끝부터
             PHONE_W x phoneScale 이다. 그만큼 왼쪽으로 당겨 오른끝을 맞춘다. -->
        <div
          class="absolute left-full top-0 origin-top-left"
          :style="{
            width: `${PHONE_W}px`,
            height: `${PHONE_H}px`,
            marginLeft: `${-PHONE_W * phoneScale}px`,
            transform: `scale(${phoneScale})`,
          }"
        >
          <div class="absolute left-0 top-0 h-[879px] w-[1031px]" :style="appear(1, { slideX: APP_PHONE_SLIDE_PX })" data-node-id="4133:20340">
            <div class="absolute contents left-[-158px] top-0" data-node-id="4133:20341">
              <div class="absolute contents left-[251px] top-[43px]" data-node-id="4133:20342">
                <div class="-translate-x-1/2 absolute contents left-[calc(50%+204.42px)] top-[43px]" data-node-id="4133:20343" data-name="iPhone">
                  <div class="absolute contents left-[-113.46px] top-[43.14px]" data-node-id="4133:20345" data-name="Group">
                    <div class="absolute h-[890.04px] left-[468.19px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-217.188px_-11.844px] mask-size-[937.834px_937.834px] top-[54.84px] w-[493.143px]" data-node-id="4133:20346" :style="style3" data-name="fix">
                      <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgFix1" />
                    </div>
                    <div class="-translate-x-1/2 -translate-y-1/2 absolute h-[937.556px] left-[calc(50%+204.42px)] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[364.461px_-0.141px] mask-size-[937.834px_937.834px] top-[calc(50%+72.42px)] w-[1666.766px]" data-node-id="4133:20347" :style="style4" data-name="🎨 change color here">
                      <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgChangeColorHere" />
                    </div>
                    <div class="absolute h-[937.556px] left-[-113.46px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[364.461px_-0.547px] mask-size-[937.834px_937.834px] top-[43.55px] w-[1666.766px]" data-node-id="4133:20348" :style="style5" data-name="display-frame">
                      <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgDisplayFrame" />
                    </div>
                    <div class="absolute contents left-[-113.46px] top-[43.55px]" data-node-id="4133:20349" data-name="Screen">
                      <div class="absolute h-[912.381px] left-[463.9px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-212.898px_-5.996px,_-577.359px_-5.441px] mask-size-[937.834px_937.834px,_1666.766px_937.556px] top-[49px] w-[493.085px]" data-node-id="4133:20351" :style="style6" data-name="👈">
                        <img alt="" class="absolute block inset-0 max-w-none size-full" height="912.381" :src="img1" width="493.085" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="absolute contents left-[-158px] top-0" data-node-id="4133:20352">
                <div class="-translate-x-1/2 absolute contents left-[calc(50%-174.65px)] top-0" data-node-id="4133:20353" data-name="iPhone">
                  <div class="absolute contents left-[-545.74px] top-[0.14px]" data-node-id="4133:20355" data-name="Group">
                    <div class="absolute h-[884.084px] left-[94.68px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-252.68px_-56.379px] mask-size-[997.697px_997.697px] top-[56.38px] w-[517.171px]" data-node-id="4133:20356" :style="style7" data-name="fix">
                      <img alt="" class="absolute block inset-0 max-w-none size-full" :src="imgFix3" />
                    </div>
                    <div class="-translate-x-1/2 -translate-y-1/2 absolute h-[997.401px] left-[calc(50%-174.66px)] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[387.742px_-0.145px] mask-size-[997.697px_997.697px] top-[calc(50%+59.35px)] w-[1773.158px]" data-node-id="4133:20357" :style="style8" data-name="🎨 change color here">
                      <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgChangeColorHere1" />
                    </div>
                    <div class="absolute h-[997.401px] left-[-545.74px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[387.742px_-0.59px] mask-size-[997.697px_997.697px] top-[0.59px] w-[1773.158px]" data-node-id="4133:20358" :style="style9" data-name="display-frame">
                      <img alt="" class="absolute inset-0 max-w-none object-cover pointer-events-none size-full" :src="imgDisplayFrame1" />
                    </div>
                    <div class="absolute contents left-[-539px] top-px" data-node-id="4133:20359" data-name="Screen">
                      <div class="absolute h-[868.256px] left-[95.86px] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-253.859px_-62.648px,_-634.859px_-61.648px] mask-size-[997.697px_997.697px,_1773.158px_997.401px] top-[62.65px] w-[518.353px]" data-node-id="4133:20361" :style="style10" data-name="👈">
                        <img alt="" class="absolute block inset-0 max-w-none size-full" height="868.256" :src="img3" width="518.353" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.app-bg {
  background-image: linear-gradient(119.01830288808736deg, rgb(0, 0, 0) 1.133%, rgb(34, 22, 2) 174.35%);
}

.app-eyebrow {
  display: inline-flex;
  align-items: center;
  border: 1px solid #ffe3af;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #ffe3af;
  white-space: nowrap;
}

/* 제목만 창 폭을 탄다. 1280 을 넘으면 원본 60px 에 걸려 더 커지지 않는다.
   (60 / 1280 * 100 = 4.6875vw — 조금 올려 잡아 1280 에서 정확히 걸리게 한다) */
.app-title {
  /* 여섯 섹션 제목을 50 으로 맞췄다(2026-09-26 요청). 원본은 60 이었다.
     어느 해상도에서도 같은 크기라야 해서, 섹션이 통째로 줄어들 때(--fit)
     제목만 그만큼 되돌려 상쇄한다. */
  font-size: calc(50px / var(--fit, 1));
  line-height: 1.1667;
  font-weight: 700;
  word-break: keep-all;
  /* 흰색 -> #ede1ca 그라데이션 글자. 원본과 같다. */
  background-image: linear-gradient(to right, #ffffff, #ede1ca);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.app-sub {
  margin: 0;
  font-size: 22px;
  line-height: 30px;
  color: #fff;
  word-break: keep-all;
}

.app-store {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 200px;
  border-radius: 5px;
  padding: 10px 15px;
  backdrop-filter: blur(15px);
  color: #fff;
  white-space: nowrap;
}
.app-store-top {
  display: block;
  font-size: 11px;
  line-height: 14px;
  opacity: 0.5;
}
.app-store-name {
  display: block;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: 0.16px;
}
</style>
